import { cn } from '@/lib/cn';
import type { MeshEnvironment } from '@/lib/env';
import { activeEnvironment } from '@/lib/environment';
import { environmentStyle } from '@/lib/environment-style';

/**
 * The persistent hairline across the top of every screen. Together with the
 * badge this is what stops an operator confusing environments.
 */
export function EnvironmentHairline() {
  const style = environmentStyle();
  return (
    <div
      aria-hidden
      className="fixed inset-x-0 top-0 z-90 h-[3px]"
      style={{ background: style.hairline }}
    />
  );
}

interface EnvironmentBadgeFaceProps {
  environment?: MeshEnvironment;
  /** Rendered after the label — a chevron when the badge is a switcher. */
  trailing?: React.ReactNode;
}

/**
 * The badge's visual, with no opinion about whether it is interactive.
 *
 * Shared so the static badge and the switcher are pixel-identical: an operator
 * must never have to work out which build they are on from the chrome.
 */
export function EnvironmentBadgeFace({
  environment = activeEnvironment,
  trailing,
}: EnvironmentBadgeFaceProps) {
  const style = environmentStyle(environment);

  return (
    <>
      <span aria-hidden className="size-1.5 rotate-45" style={{ background: style.dot }} />
      <span
        className="font-head text-[13px] font-bold uppercase"
        style={{ letterSpacing: '0.14em', color: style.text }}
      >
        {style.label}
      </span>
      {trailing}
    </>
  );
}

/** Non-interactive badge, used when the build has a single reachable target. */
export function EnvironmentBadge({ className }: { className?: string }) {
  const style = environmentStyle();

  return (
    <div
      className={cn('flex h-8 flex-none items-center gap-[7px] rounded-md px-2.5', className)}
      style={{ background: style.bg, border: `1px solid ${style.border}` }}
    >
      <EnvironmentBadgeFace />
      <span className="sr-only">You are working against the {style.label} environment.</span>
    </div>
  );
}
